import cx from "classnames";
import { EmployeeInviteLinkModal } from "modules/company/entities";
import { EmployeeInviteCreateModal } from "modules/company/features/employee-invite-create/employee-invite-create-modal";
import { useMyPermissions } from "modules/my-permissions";
import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { api, CompanyEmployeeInviteCreateDto } from "shared/api";
import { Modals } from "shared/lib";
import { Button, EntityForm, Input, Label, Select } from "shared/ui";
import styles from "./employee-invite-create-form.module.scss";

export interface IEmployeeInviteCreateForm {
  companySlug: string;

  className?: string;
  style?: CSSProperties;
  htmlId?: string;
}

export const EmployeeInviteCreateForm = (props: IEmployeeInviteCreateForm) => {
  const roles = api.companyRbac.useCompanyEmployeeRoleList(props.companySlug);
  const permissions = useMyPermissions(props.companySlug);

  api.companyInvite.useCompanyInviteInvite(props.companySlug);

  const defaultRole = useMemo(() => roles.data?.find((it) => it.type === "DEFAULT"), [roles.data]);

  const [invites, setInvites] = useState<CompanyEmployeeInviteCreateDto[]>(() => [
    { email: "", companyEmployeeRoleId: defaultRole?.id || "" },
  ]);

  useEffect(() => {
    if (defaultRole) {
      setInvites((values) =>
        values.map((it) => ({ ...it, companyEmployeeRoleId: it.companyEmployeeRoleId || defaultRole.id }))
      );
    }
  }, [defaultRole]);

  const valid = defaultRole && invites.length && invites.find((it) => it.email.trim());

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!valid) return;

    Modals.open(EmployeeInviteCreateModal, {
      companySlug: props.companySlug,
      data: { invites: invites.filter((it) => it.email.trim()) },
      onSuccess: () => setInvites([{ email: "", companyEmployeeRoleId: defaultRole?.id || "" }]),
    });
  };

  const onOpenInviteModal = () => {
    Modals.open(EmployeeInviteLinkModal, { companySlug: props.companySlug });
  };

  const hasPermission = !!permissions?.company.members.invite;

  const tabIndex = hasPermission ? undefined : -1;

  return (
    <form
      onSubmit={onSubmit}
      className={cx(styles.employee_invite_create_form, props.className)}
      style={props.style}
      id={props.htmlId}
    >
      <EntityForm
        disabled={!hasPermission}
        title="Добавить сотрудников"
        footerNote={!hasPermission && "Добавлять сотрудников может только владелец компании"}
        extraActions={
          <Button size="small" type="secondary" onClick={onOpenInviteModal} htmlType="button" tabIndex={tabIndex}>
            Ссылка для приглашения
          </Button>
        }
        primaryAction={
          <Button size="small" htmlType="submit" disabled={!valid} tabIndex={tabIndex}>
            Выслать приглашения
          </Button>
        }
      >
        <div>
          {invites.map((item, index) => (
            <div className={styles.add_employee_form} key={index}>
              <Label value="Email" className={styles.email}>
                <Input
                  placeholder="example@mail.com"
                  type="email"
                  key={index}
                  value={item.email}
                  onChange={(e) =>
                    setInvites((currentValue) => {
                      const newValue = [...currentValue];
                      newValue[index] = { ...newValue[index], email: e.target.value };
                      return newValue;
                    })
                  }
                  tabIndex={tabIndex}
                />
              </Label>
              <Label value="Роль">
                <Select
                  className={styles.select}
                  options={roles.data
                    ?.filter((it) => it.type !== "OWNER")
                    .map((it) => ({
                      value: it.id,
                      label: it.name,
                    }))}
                  value={item.companyEmployeeRoleId}
                  onChange={(companyEmployeeRoleId) =>
                    setInvites((currentValue) => {
                      const newValue = [...currentValue];
                      newValue[index] = { ...newValue[index], companyEmployeeRoleId };
                      return newValue;
                    })
                  }
                  tabIndex={tabIndex}
                />
              </Label>
            </div>
          ))}

          <Button
            size="small"
            type={"secondary"}
            onClick={() =>
              setInvites((invites) => [
                ...invites,
                {
                  email: "",
                  companyEmployeeRoleId: defaultRole?.id || "",
                },
              ])
            }
            tabIndex={tabIndex}
            htmlType="button"
          >
            Добавить еще
          </Button>
        </div>
      </EntityForm>
    </form>
  );
};
